import { toast } from 'sonner';
import { SEND_ADVISOR_ONBOARDING_INVITE_TO_OPS_MUTATION } from 'src/features/advisor-book-onboarding/api/send-onboarding-gql';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

type SendOnboardingInviteToOpsArgs = {
  readonly householdPublicId: string;
  readonly householdName: string;
  /** Runs after a successful send (e.g. the cockpit table refetches its list). */
  readonly onSuccess?: () => unknown;
};

// Sends the client's existing onboarding magic link without rotating the token or changing invite state.
export function useSendOnboardingInviteToOps() {
  const [sendToOps] = useMutationWithErrorLogging(SEND_ADVISOR_ONBOARDING_INVITE_TO_OPS_MUTATION);
  const showConfirmation = useConfirmationDialog();

  function confirmSendInviteToOps({ householdPublicId, householdName, onSuccess }: SendOnboardingInviteToOpsArgs) {
    showConfirmation({
      title: `Send to Savvy Ops — ${householdName}`,
      description: "Send a copy of the onboarding forms to Savvy Ops. Your client won't receive this email.",
      cancelLabel: 'Cancel',
      confirmationLabel: 'Send to Savvy Ops',
      onConfirmation: async () => {
        try {
          const { data } = await sendToOps({ variables: { householdPublicId } });
          const payload = data?.sendAdvisorOnboardingInviteToOps;
          if (payload?.success === true) {
            toast.success(`${householdName}'s onboarding link was sent to Savvy Ops.`);
            await onSuccess?.();
          } else {
            toast.error(
              payload?.errors?.[0]?.message ?? 'Could not send the onboarding email to Savvy Ops. Please try again.',
            );
          }
        } catch (err: unknown) {
          handleMutationError(err, 'Could not send the onboarding email to Savvy Ops. Please try again.');
        }
      },
    });
  }

  return { confirmSendInviteToOps };
}
